import axios from "axios";
import { serverErrorText } from "./serverErrorText";
import { getToken, clearToken } from "./authApi";
import { API } from "./backend";
import { installStagingInterceptors } from "./stagingGate";
import { installWriteGenerations, shareInFlight } from "./sharedRead";

export { API };

const client = axios.create({ baseURL: API, timeout: 30000 });
installStagingInterceptors(client);
installWriteGenerations(client);   // A1-95: see lib/sharedRead.js

// v0.4.6 — every /api/* route except the public allowlist requires JWT.
// Attach the token from localStorage on every call. On 401 we clear the
// stored token so AuthGate redirects the user back to /login next tick.
client.interceptors.request.use((cfg) => {
  const t = getToken();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});
client.interceptors.response.use(
  (r) => r,
  (e) => {
    // v0.6.6 — a 401 carrying `staging_locked:true` means the OUTER
    // staging-password gate re-locked, not that the real admin session
    // expired (installStagingInterceptors' own handler above already
    // clears the staging token and fires STAGING_LOCKED_EVENT for that
    // case). This handler used to check status alone, so it ran a SECOND
    // time on the same rejected response and unconditionally cleared the
    // real vat_admin_token + force-navigated to /login — wiping a still-
    // valid admin session and defeating the "re-render without a full
    // reload loop" the staging-locked event exists for.
    if (e?.response?.status === 401 && !e.response.data?.staging_locked) {
      clearToken();
      if (typeof window !== "undefined" && window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
    }
    return Promise.reject(e);
  },
);

export const listAdapters = async () => {
  const { data } = await client.get("/ingest/adapters");
  return data.adapters;
};

export const ingestCsv = async (adapter, file, declaredCurrency, options = {}) => {
  const form = new FormData();
  form.append("adapter", adapter);
  form.append("file", file);
  if (declaredCurrency) form.append("declared_currency", declaredCurrency);
  if (options.ioss_scheme) form.append("ioss_scheme", options.ioss_scheme);
  if (options.ioss_number) form.append("ioss_number", options.ioss_number);
  if (options.seller_country_override) form.append("seller_country_override", options.seller_country_override);
  if (options.price_display) form.append("price_display", options.price_display);
  if (options.default_supply_type) form.append("default_supply_type", options.default_supply_type);
  if (options.force) form.append("force", "true");
  // v0.6.8 fix — the backend's "unregistered seller country" warning at
  // upload time (_registered_countries()) used to read the global,
  // always-empty vat_app_settings.company_vat_numbers (dead since the
  // v0.4.34 multi-tenant migration), silently disabling the warning for
  // every upload. It now needs the operator's selected client to check
  // against the right registrations.
  if (options.client_id) form.append("client_id", options.client_id);
  // v0.6.8 fix — this used to share the module-level `client`'s flat
  // 30s timeout with every lightweight GET/POST. That cap covers the
  // raw network transfer time of the uploaded file itself, not just
  // server processing — a legitimately large file (real client data,
  // not just the pathological-row-cruft case already fixed
  // server-side) can take longer than 30s to transfer alone. Only
  // this call gets a longer timeout; every other endpoint keeps the
  // shared client's default.
  const { data } = await client.post("/ingest", form, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 180000,
  });
  return data;
};

export const calculate = async (payload) => {
  const { data } = await client.post("/calculate", payload);
  return data;
};

// v0.6 Stage 2 — read-only FX-requirements preview. Same payload shape
// as calculate(); never persists anything server-side. Returns
// {fx_requirements: [{currency, filing_currency, month, is_national}],
//  may_be_incomplete, transaction_count}.
export const previewFxRequirements = async (payload) => {
  const { data } = await client.post("/calculate/fx-preview", payload);
  return data;
};

// v0.4.39 T1 — async calc job API. POST /calculate/async returns 202 +
// {job_id, status:"PENDING"}; poll /calculate/jobs/{id} every 2s, then GET /result.
export const startCalcJob = async (payload) => {
  const { data } = await client.post("/calculate/async", payload);
  return data; // {job_id, status}
};
export const getCalcJob = async (jobId) => {
  const { data } = await client.get(`/calculate/jobs/${jobId}`);
  return data;
};
export const getCalcJobResult = async (jobId) => {
  const { data } = await client.get(`/calculate/jobs/${jobId}/result`);
  return data;
};
export const cancelCalcJob = async (jobId) => {
  const { data } = await client.post(`/calculate/jobs/${jobId}/cancel`);
  return data;
};

export const listIngestRuns = async () => {
  const { data } = await client.get("/runs/ingest");
  return data;
};

export const deleteIngestRun = async (runId) => {
  const { data } = await client.delete(`/runs/ingest/${runId}`);
  return data;
};

export const bulkDeleteIngestRuns = async (runIds) => {
  const { data } = await client.post(
    "/runs/ingest/bulk-delete", { run_ids: runIds },
  );
  return data;
};

export const getStaleExclusionSummary = async (staleDays = 30) => {
  const { data } = await client.get(
    "/admin/excluded-txns/staleness-summary",
    { params: { stale_days: staleDays } },
  );
  return data;
};

export const listCalcRuns = async () => {
  const { data } = await client.get("/runs/calc");
  return data;
};

export const getCalc = async (id) => {
  const { data } = await client.get(`/runs/calc/${id}`);
  return data;
};

// v0.5.2 — per-calc registration-coverage reconciliation.
export const getCalcCoverage = async (calcId) => {
  const { data } = await client.get(`/runs/calc/${calcId}/coverage`);
  return data;
};
export const acknowledgeCoverageGap = async (calcId, body) => {
  const { data } = await client.post(
    `/runs/calc/${calcId}/coverage/acknowledge`, body);
  return data;
};

// v0.5.2 — Family-B Art 45 FX-treatment amend (Tulica inclusive vs
// re-invoiced) + the amendments list for the freeze dialog / export.
export const reclassifyFxTreatment = async (calcId, body) => {
  const { data } = await client.post(
    `/runs/calc/${calcId}/reclassify-fx-treatment`, body);
  return data;
};
// v0.5.7 Stage 4 — Art 54(2)(b) work-on-movable-property resolve:
// record the physical work location (service country) on the row.
export const resolveServiceCountry = async (calcId, body) => {
  const { data } = await client.post(
    `/runs/calc/${calcId}/resolve-service-country`, body);
  return data;
};
// v0.6.11 (audit Tier 3 item 1) — Art 45 / Art 59 resolve for a B2C
// service to a consumer outside the EU: record which kind of service it
// was. The calc re-run is what reclassifies the row.
// v0.6.12 (A2-209) — answer the establishment question a row's
// CUSTOMER_ESTABLISHMENT_ASSUMED_FROM_VAT_NUMBER flag asks. Takes
// txn_ids (one or many): establishment is a fact about the CUSTOMER,
// so the natural unit is every row for the same buyer.
// v0.6.12 (A1-68) — `client`, not `authClient`. This was the only
// `authClient` reference in the file (66 calls use `client`), and the
// name is not defined or imported here, so hitting Save threw
// "authClient is not defined" before any request was made. It had
// never run: A2-209 shipped the control, but A1-63/A1-66/A1-67 showed
// the picker could not render at all until tonight, so nobody reached
// Save. The unit tests mock this module wholesale, which is why none
// of them touched the real function body.
export const resolveCustomerEstablishment = async (calcId, body) => {
  const { data } = await client.post(
    `/runs/calc/${calcId}/resolve-customer-establishment`, body,
  );
  return data;
};

// A2-484 — answer "is this rent?" (PROPERTY_LETTING_UNANSWERED) for one or many txn_ids.
export const resolvePropertyLetting = async (calcId, body) => {
  const { data } = await client.post(
    `/runs/calc/${calcId}/resolve-property-letting`, body);
  return data;
};
export const resolveArt59Category = async (calcId, body) => {
  const { data } = await client.post(
    `/runs/calc/${calcId}/resolve-art59-category`, body);
  return data;
};
// v0.5.9 Q4 — assign a tier-3 refund's channel manually. Records the
// override + reason; the calc re-run reclassifies.
export const assignRefundChannel = async (calcId, body) => {
  const { data } = await client.post(
    `/runs/calc/${calcId}/assign-refund-channel`, body);
  return data;
};

// A1-107 — one channel for the ticked credit notes (txn_ids) or for every
// open row of the card (all_open + flag_type); rows it does not fit are
// skipped and counted in the reply.
export const assignRefundChannels = async (calcId, body) => {
  const { data } = await client.post(
    `/runs/calc/${calcId}/assign-refund-channels`, body);
  return data;
};
// v0.6 — amend a buyer VAT number on a VAT_NUMBER_INVALID /
// VAT_CHECK_UNAVAILABLE row + live VIES recheck. Records the override
// (original kept) + returns the live result; the re-run reclassifies.
export const amendVatNumber = async (calcId, body) => {
  const { data } = await client.post(
    `/runs/calc/${calcId}/amend-vat-number`, body);
  return data;
};
// v0.6.10 — set the rate category on rows flagged
// MISSING_VAT_RATE_ON_DOMESTIC, per row or in bulk. Records an override
// (the uploaded figures are never mutated) and needs a calc re-run to
// take effect; the VAT itself is derived from the versioned rate table.
export const amendRateCategory = async (calcId, body) => {
  const { data } = await client.post(
    `/runs/calc/${calcId}/amend-rate-category`, body);
  return data;
};
export const getFxAmendments = async (calcId) => {
  const { data } = await client.get(
    `/runs/calc/${calcId}/fx-treatment-amendments`);
  return data;
};

// v0.5.2 — runtime system flags (VIES stub banner).
export const getSystemFlags = async () => {
  const { data } = await client.get(`/system/flags`);
  return data;
};

// v0.4.34 P1/P6 — client profile CRUD + history endpoints.
export const listClients = async () => {
  const { data } = await client.get("/clients");
  return data;
};
export const getFxMethodChoices = async () => (await client.get("/clients/fx-method-choices")).data;   // A2-543
export const getClient = async (id) => {
  const { data } = await client.get(`/clients/${id}`);
  return data;
};
export const createClient = async (body) => {
  const { data } = await client.post("/clients", body);
  return data;
};
export const updateClient = async (id, patch) => {
  const { data } = await client.patch(`/clients/${id}`, patch);
  return data;
};
export const deleteClient = async (id) => {
  await client.delete(`/clients/${id}`);
};
export const clientHistory = async (id) => {
  const { data } = await client.get(`/clients/${id}/history`);
  return data;
};
export const setRunTestFixture = async (runId, isTestFixture) => {
  const { data } = await client.patch(
    `/runs/ingest/${runId}/test-fixture`,
    { is_test_fixture: !!isTestFixture },
  );
  return data;
};
export const clientCadence = async (id) => {
  const { data } = await client.get(`/clients/${id}/cadence`);
  return data;
};

// Roadmap 3.0e — the trace of the calc on screen (each calc keeps its own).
export const traceBatch = async (ids, calcId) => {
  const { data } = await client.post("/runs/trace/batch", ids,
    calcId ? { params: { calc_id: calcId } } : undefined);
  return data;
};

export const activeRateTable = async (regime, on) => {
  const { data } = await client.get(`/rates/${regime}/active`, { params: { on } });
  return data;
};

export const xlsxExportUrl = (calcId) => `${API}/runs/calc/${calcId}/export.xlsx`;

// v0.5.0i+4 — download helpers (XLSX + IOSS/OSS XML) live in
// `lib/api/downloads.js` after the LOC-cap refactor. Re-exported
// here so every component that does
// `import { downloadCalcXlsx } from "../lib/api"` keeps working.
export {
  downloadCalcXlsx, downloadIossXml, downloadNonUnionOssXml, downloadOssXml,
  downloadIngestRun, downloadPackageZip,
  downloadClientTemplate, downloadSeMomsXml, downloadItLipeXml,
} from "./api/downloads";

// v0.4.37 L2D — Cadence Badge fuel: per-client count of PENDING
// VIES rows across UNFROZEN calcs.
export const viesPendingCount = async (clientId) => {
  try {
    const res = await client.get(`/admin/vies/pending-count/${clientId}`);
    return res.data?.pending_count || 0;
  } catch (_e) {
    return 0;
  }
};

// v0.4.37 U2 — Manual VIES retry trigger (ADMIN only).
// Returns the full run log { counters, errors, ... }.
export const retryViesPending = async (maxRows = 500) => {
  try {
    const res = await client.post(`/admin/vies/retry-pending`, { max_rows: maxRows });
    return { ok: true, ...res.data };
  } catch (e) {
    return {
      ok: false,
      error: e?.response?.data?.detail?.message
        || e?.response?.data?.detail
        || e?.message
        || "retry failed",
    };
  }
};

// v0.4.52 — TEDB-vs-bootstrap rate verification (ADMIN only).
// Returns { ok, status, body, error? }.
//   200 → ok=true, every form's bootstrap matches TEDB.
//   202 → ok=false, body has the per-form discrepancies.
//   503 → ok=false with `error` message (TEDB unreachable).
export const verifyRatesAgainstTedb = async () => {
  try {
    const res = await client.post(`/admin/rates/verify-against-tedb`);
    return { ok: res.status === 200, status: res.status, body: res.data };
  } catch (e) {
    return {
      ok: false,
      status: e?.response?.status || 0,
      error: e?.response?.data?.detail?.message
        || e?.response?.data?.detail
        || e?.message
        || "verify failed",
    };
  }
};

// v0.4.53 — Drift surveillance history (ADMIN only). Returns the
// last N scheduler/operator drift-check runs + the latest one.
export const fetchDriftRuns = async (limit = 30) => {
  try {
    const res = await client.get(`/admin/rates/drift-runs?limit=${limit}`);
    return { ok: true, body: res.data };
  } catch (e) {
    return {
      ok: false,
      status: e?.response?.status || 0,
      error: e?.response?.data?.detail?.message
        || e?.response?.data?.detail
        || e?.message,
    };
  }
};

// v0.6.1 — Pending TEDB rate-drift acks (ADMIN only). Revived counterpart
// to verifyRatesAgainstTedb's pending_acks_created: lists the open
// pending_rate_acks docs so an operator can actually review one.
export const fetchPendingRateAcks = async () => {
  try {
    const res = await client.get(`/admin/rates/acks`);
    return { ok: true, body: res.data };
  } catch (e) {
    return {
      ok: false,
      status: e?.response?.status || 0,
      error: e?.response?.data?.detail?.message
        || e?.response?.data?.detail
        || e?.message,
    };
  }
};

// v0.6.1 — Resolve one pending rate ack.
// body: { decision: "no_amendment_needed"|"amendment_required",
//          reason: <min 50 chars>, amendment_ref?: string }
export const resolveRateAck = async (ackId, body) => {
  try {
    const res = await client.post(`/admin/rates/acks/${ackId}`, body);
    return { ok: true, body: res.data };
  } catch (e) {
    return {
      ok: false,
      status: e?.response?.status || 0,
      error: e?.response?.data?.detail?.message
        || e?.response?.data?.detail
        || e?.message,
    };
  }
};

// v0.4.54 — Stale-surveillance alarm. ADMIN-only. Returns:
//   { state: "OK"|"STALE"|"BROKEN", last_successful_run_at, age_hours,
//     thresholds: {stale_hours, broken_hours}, latest_outcome }
export const fetchDriftHealth = async () => {
  try {
    const res = await client.get(`/admin/health/drift`);
    return { ok: true, body: res.data };
  } catch (e) {
    return { ok: false, status: e?.response?.status || 0 };
  }
};

// v0.4.37 U3 — VIES retry stats (last 30 runs).
export const viesRetryRuns = async (limit = 30) => {
  try {
    const res = await client.get(`/admin/vies/retry-runs?limit=${limit}`);
    return res.data?.runs || [];
  } catch (_e) {
    return [];
  }
};

// v0.4.37 L1=c — pre-export lint for OSS XML.
// Returns { ok, offender_count, offenders }. Never throws — failures
// degrade to { ok: false, offender_count: 0, offenders: [], error }.
export const lintOssXml = async (calcId) => {
  try {
    const res = await client.get(`/ossfilings/${calcId}/lint`);
    return res.data;
  } catch (e) {
    return {
      ok: false, offender_count: 0, offenders: [],
      error: e?.response?.data?.detail || e?.message || "lint failed",
    };
  }
};

// v0.4.37 Task B — ADMIN-only batch apply of operator-picked OSS
// VAT rates. `corrections` is a list of `{id, rate_type}` items.
// Server responds with `{applied_count, applied, errors, lint}`.
// Re-throws on HTTP error so the modal can render the detail
// (CALC_FROZEN 409 surfaces inline).
export const applyOssLint = async (calcId, corrections) => {
  const res = await client.post(
    `/ossfilings/${calcId}/lint/apply`,
    { corrections },
  );
  return res.data;
};

// v0.4.38 N4 — POST /api/calc/{id}/reconcile.
// Multipart upload of a reference XLSX. Returns row-level delta.
// Rethrows on HTTP error so the panel can render the 400 detail
// (UNKNOWN_COLUMNS / INVALID_NET / INVALID_COUNTRY).
export const apiReconcile = async (calcId, file) => {
  const fd = new FormData();
  fd.append("file", file);
  const res = await client.post(`/calc/${calcId}/reconcile`, fd, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 60000,
  });
  return res.data;
};

// v0.4.45 — Amendment-tracking API.
export const listAmendments = async ({ status, overdue } = {}) => {
  const params = {};
  if (status) params.status = status;
  if (overdue !== undefined) params.overdue = overdue;
  const { data } = await client.get("/admin/amendments", { params });
  return data;
};
export const openManualAmendment = async (body) => {
  const { data } = await client.post("/admin/amendments", body);
  return data;
};
export const recordAmendmentSubmission = async (id, body) => {
  const { data } = await client.post(`/admin/amendments/${id}/submission`, body);
  return data;
};
export const resolveAmendment = async (id, body) => {
  const { data } = await client.post(`/admin/amendments/${id}/resolve`, body);
  return data;
};
export const appendAmendmentNote = async (id, text) => {
  const { data } = await client.post(`/admin/amendments/${id}/notes`, { text });
  return data;
};
export const amendmentAudit = async (id) => {
  const { data } = await client.get("/admin/amendments/audit", {
    params: { amendment_id: id },
  });
  return data;
};

// v0.4.47 — Per-country VAT-return API. A1-95: summary calls made together share one request.
export const getCountryReturnSummary = (calcId) => shareInFlight(`country-return-summary|${calcId}`,
  async () => (await client.get(`/calc/${calcId}/country-return-summary`)).data);
export const getCountryReturn = async (calcId, country) => {
  const { data } = await client.get(
    `/calc/${calcId}/country-return/${country}`,
  );
  return data;
};
export const patchCountryReturnBox = async (calcId, country, boxId, body) => {
  const { data } = await client.patch(
    `/calc/${calcId}/country-return/${country}/box/${boxId}`, body,
  );
  return data;
};

export const submitCountryReturnClarification = async (calcId, country, group, interpretation) => {
  const { data } = await client.post(
    `/calc/${calcId}/country-return/${country}/clarify/${group}`,
    { interpretation },
  );
  return data;
};

// v0.4.71d — IT LIPE chain audit history endpoint.
// v0.6.13 (A1-51) — purchase questions: facts an invoice does not show.
export const answerPurchases = async (calcId, body) => {
  const { data } = await client.post(`/calc/${calcId}/purchase-answers`, body);
  return data;
};

// Expense types are chosen per purchase row on the country return.
export const getPurchaseRows = async (calcId, country) => {
  const { data } = await client.get(`/calc/${calcId}/purchase-rows`, { params: { country } });
  return data;
};

export const setPurchaseTypes = async (calcId, body) => {
  const { data } = await client.post(`/calc/${calcId}/purchase-types`, body);
  return data;
};

export const getLipeChainAudit = async (calcId) => {
  const { data } = await client.get(
    `/calc/${calcId}/country-return/IT/chain-audit`,
  );
  return data;
};

// v0.4.84 — Reconciliation-offender diagnostic actions.
// Given an offender (country + rate) from the ActionRequiredBanner,
// the operator can either inspect the txns at that rate (bad-data
// path) or flag the rate-table for repo-maintainer attention
// (genuine-drift path). See routes_recon_diagnostics.py.
export const getTransactionsAtRate = async (calcId, country, rate) => {
  const { data } = await client.get(
    `/calc/${calcId}/transactions/at-rate`,
    { params: { country, rate } },
  );
  return data;
};

export const flagRateTableDrift = async (calcId, body) => {
  const { data } = await client.post(
    `/calc/${calcId}/rate-table-drift-flags`, body,
  );
  return data;
};

export const listRateTableDriftFlags = async (calcId) => {
  const { data } = await client.get(
    `/calc/${calcId}/rate-table-drift-flags`,
  );
  return data;
};

// v0.5.0c — Supersede a calc, optionally excluding specific txn
// ids from the rerun. Used by the Inspect-drawer
// "Supersede these N transactions →" path. Returns
// `{ new_calc_id, supersedes_calc_id, reason, signoff_id }`.
// v0.6.10 fix — these two RE-RUN THE WHOLE CALCULATION server-side
// (both supersede the old calc), so they are nothing like the
// lightweight GET/POSTs the shared 30s client timeout was sized for. On
// a real file — 951 rows in the case that surfaced this — the re-run
// outlasts 30s and the operator sees "timeout of 30000ms exceeded" on a
// request that is still succeeding on the server, leaving them unsure
// whether the drop applied. Same carve-out the /ingest upload already
// has, and for the same reason.
//
// v0.6.10 follow-up — 180000 was still too small, and the failure mode
// is worse than "unsure". MEASURED on the GB calc that prompted this:
// the supersede ran 06:59:14 -> 07:02:19, about 185s, against a 180s
// client timeout. The browser aborted a request the backend then
// completed with 200 OK. The operator saw a button stuck on
// "Dropping…", concluded nothing had happened, and clicked again — so
// the drop ran TWICE, leaving 831 registry entries where one run makes
// 415. A client that abandons a DESTRUCTIVE server operation it cannot
// cancel invites the operator to repeat it.
//
// 900000 (15 min) is deliberate headroom over the measured 185s, not a
// tuned value: this endpoint is synchronous by construction
// (routes_supersede.py awaits calculate() inline), so the only honest
// options are "wait" or "make it a job with progress, like the main
// calc path". The latter is the real fix; this stops the duplicate
// destructive runs until then.
// v0.6.10 — BACKGROUND supersede. Prefer this over supersedeCalc below.
//
// The sync endpoint blocks for the whole recalc (~185s measured), which
// is how a 180s client timeout came to abandon a destructive run the
// server then completed — leaving the operator to click again and drop
// the same rows twice. This starts a job and polls the SAME
// /calculate/jobs/{id} endpoint the main calc path already uses, so the
// operator sees real per-phase progress instead of a frozen button.
//
// Returns the job's `result_payload` — i.e. the identical
// `{ new_calc_id, supersedes_calc_id, reason, signoff_id }` the sync
// route returns — so callers need no other change.
//
// A 409 SUPERSEDE_IN_PROGRESS carries the in-flight `job_id`; this
// ATTACHES to that run rather than failing, because the operator
// almost certainly just double-clicked and the honest answer is "the
// thing you asked for is already happening", not an error.
export const supersedeCalcAsync = async (oldCalcId, body, onProgress) => {
  let jobId;
  try {
    const { data } = await client.post(
      `/runs/calc/${oldCalcId}/supersede/async`, body,
    );
    jobId = data?.job_id;
  } catch (e) {
    const d = e?.response?.data?.detail;
    if (e?.response?.status === 409 && d?.error === "SUPERSEDE_IN_PROGRESS"
        && d?.job_id) {
      jobId = d.job_id;
    } else {
      throw e;
    }
  }
  if (!jobId) throw new Error("supersede did not return a job id");
  return pollSupersedeJob(jobId, onProgress);
};

// Poll until terminal. No wall-clock cap: the job is running on the
// server whether or not we are watching, and giving up on the watch
// is exactly what produced the duplicate-drop incident. The operator
// can navigate away; the run completes regardless.
export const pollSupersedeJob = async (jobId, onProgress) => {
  for (;;) {
    // eslint-disable-next-line no-await-in-loop
    const job = await getCalcJob(jobId);
    if (onProgress) onProgress(job);
    const status = job?.status;
    if (status === "COMPLETE") {
      // eslint-disable-next-line no-await-in-loop
      const { data } = await client.get(`/calculate/jobs/${jobId}/result`);
      return data;
    }
    if (status === "FAILED" || status === "CANCELLED") {
      // v0.6.12 — the message is the server's sentence, not its code
      // (lib/serverErrorText.js); the code stays on err.code for callers.
      const d = job?.error?.detail;
      const err = new Error(
        serverErrorText(d, `supersede ${String(status).toLowerCase()}`),
      );
      err.code = d?.error;
      err.jobError = job?.error;
      throw err;
    }
    // eslint-disable-next-line no-await-in-loop
    await new Promise((r) => { setTimeout(r, 2000); });
  }
};

export const supersedeCalc = async (oldCalcId, body) => {
  const { data } = await client.post(
    `/runs/calc/${oldCalcId}/supersede`, body, { timeout: 900000 },
  );
  return data;
};

// v0.5.0c+2 — Per-country active rates for the ReasonPicker
// token-filler dropdown. Resolves the rate-table version that
// covers `on` (YYYY-MM-DD) and returns
// `{country_code, regime, rate_table_version, on,
//   rates: [{key, rate_decimal, rate_pct, label, display}, ...]}`.
export const getCountryRates = async (countryCode, onDate, regime) => {
  const params = new URLSearchParams({ on: onDate });
  if (regime) params.set("regime", regime);
  const { data } = await client.get(
    `/rates/country/${countryCode}?${params.toString()}`,
  );
  return data;
};

// v0.5.0d — Manual-override routing for unknown-rate offenders.
// Bulk-first: accepts an array of txn ids that all route to the
// same manual-override box. Endpoint validates the country has a
// manual box (DE/FR only — ES/UK/IT 422). Internally supersedes
// the old calc with the txns excluded, then stamps the override
// row so the manual box renders with sum(net) of the routed txns.
// Returns `{ new_calc_id, supersedes_calc_id, country,
//            manual_box_id, overrides_created,
//            manual_box_net, manual_box_vat }`.
// v0.6.10 — background variant of routeTxnsManualOverride. This action
// supersedes the calc internally, so it blocked for exactly as long as
// the drop did and its button froze the same way. Same job machinery,
// same in-flight attach-don't-fail behaviour.
export const routeTxnsManualOverrideAsync = async (
  oldCalcId, body, onProgress,
) => {
  let jobId;
  try {
    const { data } = await client.post(
      `/runs/calc/${oldCalcId}/txn-override/async`, body,
    );
    jobId = data?.job_id;
  } catch (e) {
    const d = e?.response?.data?.detail;
    if (e?.response?.status === 409 && d?.error === "SUPERSEDE_IN_PROGRESS"
        && d?.job_id) {
      jobId = d.job_id;
    } else {
      throw e;
    }
  }
  if (!jobId) throw new Error("txn-override did not return a job id");
  return pollSupersedeJob(jobId, onProgress);
};

export const routeTxnsManualOverride = async (oldCalcId, body) => {
  // See supersedeCalc — this supersedes the calc too.
  const { data } = await client.post(
    `/runs/calc/${oldCalcId}/txn-override`, body, { timeout: 900000 },
  );
  return data;
};

// v0.5.0f — Server-generated audit-reason text for the redesigned
// Inspect drawer. `decision` is one of `real_rate_single`,
// `fix_upstream`, `bulk_route`. Returns `{ decision, country,
// rate, txn_count, reason }`.
export const getInspectReason = async (calcId, { country, rate, decision }) => {
  const params = new URLSearchParams({ country, rate, decision });
  const { data } = await client.get(
    `/calc/${calcId}/inspect-reason?${params.toString()}`,
  );
  return data;
};

// Roadmap 3b — the tax authority's file for a country return, and the
// check of the return against it.
export const listAuthorityFiles = async (calcId, country) =>
  (await client.get(`/calc/${calcId}/country-return/${country}/authority-files`)).data;
export const uploadAuthorityFile = async (calcId, country, file) => {
  const fd = new FormData();
  fd.append("file", file);
  return (await client.post(`/calc/${calcId}/country-return/${country}/authority-file`, fd, {
    headers: { "Content-Type": "multipart/form-data" }, timeout: 60000,
  })).data;
};
export const deleteAuthorityFile = async (calcId, country, fileId) =>
  (await client.delete(`/calc/${calcId}/country-return/${country}/authority-file/${fileId}`)).data;
export const authorityCheck = async (calcId, country) =>
  (await client.get(`/calc/${calcId}/country-return/${country}/authority-check`)).data;

// Roadmap 4.1 — what the client's last filed period had that this upload lacks.
export const channelContinuity = async (calcId) =>
  (await client.get(`/runs/calc/${calcId}/channel-continuity`)).data;

// Roadmap 5.1 — this run re-does a filed period: filed vs corrected, and how
// the country wants it corrected. params: found_on, current_box6, prior_year.
// Roadmap 5.1 — rows in this upload dated in a period already filed.
export const filedPeriodOverlap = async (calcId) =>
  (await client.get(`/runs/calc/${calcId}/filed-period-overlap`)).data;
export const correctionOf = async (calcId, country, params = {}) =>
  (await client.get(`/runs/calc/${calcId}/correction/${country}`, { params })).data;
